import React from 'react';
import { DownloadIcon } from 'components/icons';
import { t } from '@lingui/macro';
import { registerDownload } from 'utils/tracking/registerDownload';
import { Button, ButtonProps, forwardRef, Link } from '@cardboard-ui/react';
import { graphql, useFragment } from 'utils/graphClient';
import { DownloadButton_node$key } from './__generated__/DownloadButton_node.graphql';
import { useNodeSecurity } from 'apps/TenantApp/hooks/useNodeSecurity';
import { useItem } from 'components/ItemHandle';
import { postMessageWTC } from 'apps/TenantApp/screens/ItemViewer/FileRenderer/utils';
import { Capacitor } from '@capacitor/core';

const nodeFragment = graphql`
  fragment DownloadButton_node on Node {
    __typename
    id
    ...useItemHandleFragment
    ...useNodeSecurity_node
    ... on FolderItem {
      content {
        ... on File {
          id
          url
        }
      }
    }
    ... on VaultItem {
      content {
        ... on File {
          url
        }
      }
    }
  }
`;

interface DownloadButtonProps extends Omit<ButtonProps, 'children'> {
  node: DownloadButton_node$key;
  closeMenu?: () => void;
  webViewerNotAvailable?: boolean;
  inItemViewer?: boolean;
}

export const DownloadButton = ({
  node,
  closeMenu,
  webViewerNotAvailable,
  inItemViewer,
  ...props
}: DownloadButtonProps) => {
  const { id, content, ...fragments } = useFragment(nodeFragment, node);
  const { isDownloadPrevented, hasWatermark } = useNodeSecurity(fragments);

  const mobileWatermark = Capacitor.isNativePlatform() && hasWatermark;

  if (isDownloadPrevented || mobileWatermark) {
    return <DisabledDownloadButton {...props} />;
  } else if (hasWatermark) {
    return (
      <WatermarkDownloadButton
        node={node}
        webViewerNotAvailable={webViewerNotAvailable}
        inItemViewer={inItemViewer}
        {...props}
      />
    );
  } else {
    return (
      <DefaultDownloadButton node={node} closeMenu={closeMenu} {...props} />
    );
  }
};

const DisabledDownloadButton = (props: ButtonProps) => (
  <InnerButton isDisabled {...props}>{t`Download disabled`}</InnerButton>
);

const WatermarkDownloadButton = ({
  node,
  closeMenu,
  webViewerNotAvailable,
  inItemViewer,
  ...props
}: DownloadButtonProps) => {
  const downloadNode = useFragment(nodeFragment, node);
  const { linkProps } = useItem({ item: downloadNode });

  if (isWatermarkDownloadAvailable()) {
    return (
      <InnerButton
        onClick={() => {
          downloadFileViaWebViewer();
          registerDownload(downloadNode.id);
          closeMenu && closeMenu();
        }}
        {...props}
      />
    );
  } else if (webViewerNotAvailable || inItemViewer) {
    return (
      <InnerButton
        isDisabled
        {...props}
      >{t`Download not available`}</InnerButton>
    );
  } else {
    return (
      <InnerButton
        as={Link}
        {...linkProps}
        {...props}
      >{t`Open preview to download`}</InnerButton>
    );
  }
};

const DefaultDownloadButton = ({
  node,
  closeMenu,
  ...props
}: DownloadButtonProps) => {
  const downloadNode = useFragment(nodeFragment, node);
  const downloadLink = downloadNode.content?.url;

  if (!downloadLink) {
    return null;
  }

  return (
    <InnerButton
      as={Link}
      onClick={() => {
        registerDownload(downloadNode.id);
        closeMenu && closeMenu();
      }}
      toDownload={downloadLink}
      {...props}
    />
  );
};

const InnerButton = forwardRef<ButtonProps, 'button'>((props, ref) => (
  <Button
    ref={ref}
    rightIcon={<DownloadIcon />}
    justifyContent="space-between"
    children={t`Download`}
    {...props}
  />
));

const isWatermarkDownloadAvailable = () => {
  return document.getElementById('web-viewer');
};

function downloadFileViaWebViewer() {
  postMessageWTC({
    type: 'downloadFile',
  });
}
